import React from 'react'
import { Tooltip } from 'react-tooltip'
import { useLocalization } from '../src/hooks/useLocaleFormatter'
import { useTranslate } from '../src/hooks/useTranslate'
import { ProductPriceFragment, ProductTypeEvaluationFragment, ProductUnit, ProductVariantFragment } from '../src/model/fragments'
import { getLocale } from '../src/model/locale'
import { multiplyPrices } from '../src/utils/formatPrice'
import { useIsVatPreferred } from '../src/utils/useIsVatPreferred'
import { useQuantityInput } from '../src/utils/useQuantityInput'
import { AddToShoppingListButton } from './AddToShoppingListButton'
import { Button, ButtonView } from './Button'
import SvgArrowDown from './icons/SvgArrowDown'
import { ProductVariantPickerExpanded } from './ProductVariantPickerExpanded'
import { QuantityInput } from './QuantityInput'
import { FlexiLink } from './FlexiLink'
import { getProductCategoryLink } from '../src/model/urls'

export interface ProductLayoutProps {
	id: string
	name: string
	altName?: string
	unit?: ProductUnit
	quantityPerLayer?: number
	quantityPerSqm?: number
	palletLayerSqm?: number
	complementaryDimensions?: string
	description: string | undefined
	pills: { label?: string, anchor?: string}[],
	productTypes: ProductTypeEvaluationFragment,
	attributes: React.ReactNode
	dimensions: string
	parameters: React.ReactNode
	gallery: React.ReactNode | undefined
	variants: ProductVariantFragment[]
	selectedVariantId: null | string
	onSelectedVariantIdChange: (id: null | string) => void
}

const ProductLayoutDescription: React.FunctionComponent<
	Pick<ProductLayoutProps, 'description' | 'productTypes'>
> = ({ productTypes, ...props }) => {
	const description = props.description || ''
	const localization = useLocalization()

	const offset = 300
	const lastDot =
		description.substr(offset).indexOf('. ') + 1 || description.length
	const descriptionPreview = description.substr(0, offset + lastDot)
	const descriptionNeedsExpanding =
		descriptionPreview.length < description.length

	const [isExpanded, setIsExpanded] = React.useState(!descriptionNeedsExpanding)

	if(!description?.trim())
	{
		return <></>
	}

	return (
		<>
			<div className="productLayout-description">
				{isExpanded ? description : descriptionPreview}{' '}
				{!isExpanded && (
					<div className="productLayout-expand">
						<button
							type="button"
							className="productLayout-expand-button"
							onClick={() => setIsExpanded(true)}
						>
							{localization.translate('product-showMore-label')}
						</button>
					</div>
				)}
			</div>
		</>
	)
}

export const ProductLayout: React.FunctionComponent<ProductLayoutProps> = ({
	id,
	name,
	altName,
	unit,
	quantityPerLayer,
	quantityPerSqm,
	palletLayerSqm,
	description,
	complementaryDimensions,
	productTypes,
	attributes,
	dimensions,
	parameters,
	gallery,
	variants,
	pills,
	selectedVariantId,
	onSelectedVariantIdChange,
}) => {
	const translate = useTranslate()
	const localization = useLocalization()
	const isVatPreferred = useIsVatPreferred();
	const [quantityInput, setQuantityInput] = useQuantityInput(id)
	const quantity = ((unit && [ProductUnit.sqm, ProductUnit.ton].includes(unit)) ? parseFloat(quantityInput.value) : parseInt(quantityInput.value)) || 0

	const variant = React.useMemo(
		() => variants.find((it) => it.id === selectedVariantId),
		[variants, selectedVariantId],
	)

	const price = React.useMemo(() => {
		const basePrices = localization.extractPrices(
			(variant ? variant : variants[0]).prices,
		)
		const baseQuantity = variant ? quantity : 0

		return {
			vat: localization.formatPrices(multiplyPrices(basePrices, baseQuantity), 1, true) + (isVatPreferred ? '' : (' ' + translate('inclVAT'))),
			noVat: localization.formatPrices(multiplyPrices(basePrices, baseQuantity), 1, false) + (isVatPreferred ? (' ' + translate('noVAT')) : ''),
		}
	}, [localization, variants, variant, quantity])

	const originalPrice = React.useMemo(() => {
		if(!variant?.discounts.length)
		{
			return price;
		}

		const basePrices = localization.extractPrices(
			(variant ? variant : variants[0]).originalPrices,
		)
		const baseQuantity = variant ? quantity : 0

		return {
			vat: localization.formatPrices(multiplyPrices(basePrices, baseQuantity), 1, true) + (isVatPreferred ? '' : (' ' + translate('inclVAT'))),
			noVat: localization.formatPrices(multiplyPrices(basePrices, baseQuantity), 1, false) + (isVatPreferred ? (' ' + translate('noVAT')) : ''),
		}
	}, [localization, variants, variant, quantity, price])

	const buttonText = React.useMemo(() => {

		if(price && variant && quantityInput.value)
		{
			return <>
				{ translate('productLayout-addToShoppingList') + ' ' + (isVatPreferred ? price.vat : price.noVat) + ' ' + (isVatPreferred ? translate('inclVAT') : translate('noVAT')) }
			</> 
		}
		else
		{
			let lowest : (ProductPriceFragment | null) = null;
			(variant ? [variant] : variants).forEach(v => {
				const basePrices = localization.extractPrices(
					v.prices,
				);

				basePrices.forEach(basePrice => {
					if(!lowest || basePrice.cents < lowest.cents)
					{
						lowest = basePrice;
					}
				});
			});

			if(quantityInput.value && lowest)
			{
				const l = lowest as ProductPriceFragment;
				lowest = {
					...l, 
					cents: l.cents * parseFloat(quantityInput.value)
				}
			}

			if(lowest)
			{
				const formatted = {
					vat: localization.formatPrice(lowest, 1, true) + (isVatPreferred ? '' : (' ' + translate('inclVAT'))),
					noVat: localization.formatPrice(lowest, 1, false) + (isVatPreferred ? (' ' + translate('noVAT')) : ''),
				};

				return <>
					{ translate('productLayout-addToShoppingListFrom') }
				</>
			}
			
			return undefined;
		}

	}, [price, variants, variant, quantity]);

	const getTitle = React.useCallback((variant : ProductVariantFragment) => {
		const productTitle = `${name} ${altName}`.trim();
		const variantTitle = (getLocale(variant.color?.locales, localization.possibleLocales)?.name + ' ' + getLocale(variant.surface?.locales, localization.possibleLocales)?.name).trim();

		return [productTitle, variantTitle].filter(i => i).join(', ');
	}, [localization]);

	const dimensionsValid = React.useMemo(() => {

		if(
			dimensions.includes('__ x') || 
			dimensions.includes('x __') || 
			dimensions.startsWith(' 0 x') ||
			dimensions.endsWith('x 0') ||
			dimensions.includes('x 0 x')
		)
		{
			return false;
		}

		return true;
	}, [dimensions]);

	const highlightVariantPicker = variant === undefined
	const highlightQuantityInput = quantity === 0
	return (
		<section className="productLayout">
			<div className="productLayout-in">

			<div className="productLayout-aside">
				{gallery && (
					<div className="productLayout-aside-in">{gallery}</div>
				)}
			</div>


			<div className="productLayout-header">
				<div className="productLayout-header-attributes">{attributes}</div>
				<h1>
					<span className="productLayout-header-name">{name}</span>
					<span className='sr-only'>, </span>
					<span className='productLayout-header-sub'>
						{altName!.trim()} {dimensionsValid ? dimensions : ''}
					</span>
				</h1>
				{
					complementaryDimensions && (complementaryDimensions?.trim() != altName?.trim()) && <div className="productLayout-header-complementaryDimensions">{complementaryDimensions}</div>
				}
			</div>
			

				<div className="productLayout-main">
					<div className='productLayout-main-productTypes' style={{justifySelf: 'flex-end', position: 'relative', top: '35px'}}>
						<em>{translate('produktove-typy-detail')}</em>
						<ul>
							{
								[
									productTypes.system,
									productTypes.default,
									...productTypes.ancestors, 
									...productTypes.additional
								].filter(x => x && x.link?.url).map(t => {
									const data = getLocale(t?.locales, localization.possibleLocales);
									return <li><FlexiLink nextLink={getProductCategoryLink(localization.locale, t!.id, t!.link.url)}><a>
										{ data?.name }
									</a></FlexiLink></li>;
								})
							}
						</ul>
					</div>

					<div className="productLayout-box">
						<div className="productLayout-variants">
							<p className='productLayout-label'><strong>{translate('variant')}</strong></p>
							<ProductVariantPickerExpanded
								items={variants}
								unit={unit}
								selectedId={selectedVariantId}
								onChange={onSelectedVariantIdChange}
								highlight={highlightVariantPicker}
							/>
						</div>
						<div className="productLayout-options">
							
							<div>
								<div>
									<p className='productLayout-label'><strong>{translate('quantity')}</strong></p>
									<QuantityInput
										preferredUnits={unit !== ProductUnit.sqm}
										quantityPerSqm={quantityPerSqm}
										quantityPerLayer={quantityPerLayer}
										palletLayerSqm={palletLayerSqm}
										value={quantityInput}
										setValue={setQuantityInput}
										highlight={highlightQuantityInput}
									/>
								</div>

								<div className='productLayout-button-wrap'>
									<p className='productLayout-label productLayout-label-title'>
										<strong>{ variant ? ( getTitle(variant)) : ''}</strong>
									</p>
									<AddToShoppingListButton
										variantId={variant && variant.id}
										quantityInput={quantityInput.value}
										onClick={() => setQuantityInput('')}
										customText={ buttonText }
										customAltText={ price.noVat }
										block
									/>
								</div>
							</div>
						</div>

						{
							variant && variant.discounts.length > 0 && quantityInput.value && 
							<div className='productLayout-discount'>
								<span className='productLayout-discount-label'>{translate('discounted-price')}</span>
								<span className='productLayout-discount-original-price'>{translate('original-price')}: <s>{ isVatPreferred ? (originalPrice.vat + ' ' + translate('inclVAT')) : (originalPrice.noVat + ' ' +  translate('noVAT'))}</s></span>
							</div>
						}
						
					</div>

					<ProductLayoutDescription
						description={description}
						productTypes={productTypes}
					/>
				</div>

				<div className="productLayout-pills">
					<div>
						{pills.map((item, i) => (
							<div className='productLayout-pills-in' data-tooltip-content={ translate('prejit') }>
								<Button
									key={i}
									view={ButtonView.tabs}
									
									onClick={() => document.getElementById(item.anchor!)?.scrollIntoView({behavior: 'smooth'})}
									icon={<SvgArrowDown/>}
								>
										{item.label}
								</Button>
							</div>
						))}
						<Tooltip anchorSelect=".productLayout-pills-in" />
					</div>
				</div>

				<div className="productLayout-parameters">
					<p className='productLayout-label'><strong>{translate('technicalSpecifications')}</strong></p>	
					{parameters}
				</div>
			</div>
		</section>
	)
}
